import React, { useState, useMemo, useRef, useEffect } from 'react';
import { IconButton } from 'components/form/IconButton/IconButton';
import InfoMessage from 'components/Commons/InfoMessage/InfoMessage.component';
import { TableCellValue, TableData } from './TableData.model';
import { isNumericLikePrimitive } from 'helpers/tableTrustPolicy';
import { isValidUrl } from 'utils/location.utils';
import ClipboardService from 'services/ClipboardService';
import { downloadXlsx } from 'utils/exportXlsx';
import './TableView.scss';

export type TableViewProps = {
  data: TableData;
  caption?: string;
  sortable?: boolean;
  striped?: boolean;
  bordered?: boolean;
  hover?: boolean;
  responsive?: boolean;
  maxHeight?: string;
  className?: string;
  showExportButtons?: boolean;
  isFilled?: boolean;
  exportFormats?: ('csv' | 'json' | 'excel')[];
  filename?: string;
  onExport?: (format: string, data: TableData) => void;
  /** If true, cells with verified=false are hidden (replaced with —). Default: false */
  hideUnverified?: boolean;
};

type SortConfig = {
  key: string;
  direction: 'ascending' | 'descending';
} | null;

const getCellValue = (cellData: string | number | null | TableCellValue): string | number | null => {
  if (cellData && typeof cellData === 'object' && 'value' in cellData) {
    return cellData.value;
  }
  return cellData;
};

export const formatNumber = (value: number): string => {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
}

const parseUrlsInText = (text: string): React.ReactNode => {
  const urlRegex = /(https?:\/\/(?:[-\w.])+(?:\:[0-9]+)?(?:\/(?:[\w\/_.])*)?(?:\?(?:[\w&%_.])*)?(?:\#(?:[\w.])*)?)/gi;

  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (isValidUrl(part)) {
      const domain = new URL(part).hostname.replace('www.', '');

      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="table-view__link"
          title={part}
        >
          {domain}
        </a>
      );
    }
    return part;
  });
};

export type ExportValueOptions = {
  /** 'all' = append source links to every cell; 'stringsOnly' = skip numeric cells so
   *  number columns stay calculable. Default: 'all'. */
  linkScope?: 'all' | 'stringsOnly';
  /** Append the CAGR/derivation formula (the cell tooltip) in parentheses. Default: true. */
  includeFormula?: boolean;
};

export const cleanValueForExport = (
  cellData: string | number | null | TableCellValue,
  options: ExportValueOptions = {}
): string => {
  const { linkScope = 'all', includeFormula = true } = options;
  const value = getCellValue(cellData);

  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  const htmlLinkRegex = /<a\s+([^>]*\s+)?href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
  const cleanedValue = stringValue.replace(htmlLinkRegex, (_, _attrs, href, text) => `${text} (${href})`);

  if (cleanedValue === 'N/A' || cleanedValue === '') {
    return '';
  }

  // Preserve the structured metadata that the GUI shows but plain export otherwise drops:
  // the source hyperlink and the CAGR/derivation formula (cell tooltip). Single source of
  // truth for every export path, so all of them stay in sync.
  const cell = cellData && typeof cellData === 'object' ? (cellData as TableCellValue) : null;
  let out = cleanedValue;

  // Append the source link only when the GUI would actually show one: the cell is a
  // verified source (verified === true + sourceUrl) and is NOT a value we calculated
  // (a calculated value links to base data, which is misleading). Numeric cells are also
  // skipped under linkScope='stringsOnly' so number columns stay calculable.
  const sourceUrl = cell?.sourceUrl;
  const isCalculated = !!(cell && (cell.calculationDetails || cell.filled));
  const hasVerifiedSource = cell?.verified === true && typeof sourceUrl === 'string' && sourceUrl.trim().length > 0;
  const linkAllowedForType = linkScope === 'all' || !isNumericLikePrimitive(value);
  if (hasVerifiedSource && !isCalculated && linkAllowedForType && !out.includes(sourceUrl as string)) {
    out += ` (${sourceUrl})`;
  }
  if (includeFormula && cell?.calculationDetails) {
    out += ` (${cell.calculationDetails})`;
  }
  return out;
};

// Copy / calculation-friendly sheet: numbers stay bare, strings keep their link.
const COPY_EXPORT_OPTS: ExportValueOptions = { linkScope: 'stringsOnly', includeFormula: false };

const TableView: React.FC<TableViewProps> = ({
  data,
  caption,
  sortable = false,
  striped = true,
  bordered = true,
  hover = true,
  responsive = true,
  isFilled = false,
  maxHeight,
  className = '',
  showExportButtons = false,
  exportFormats = ['csv', 'json'],
  filename = 'table-data',
  onExport,
  hideUnverified = false
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [copied, setCopied] = useState<boolean>(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hide columns where every row is empty (e.g., LLM emitted a header but never populated it).
  // Single-column tables and empty tables are left untouched. Exports also use only visible headers.
  const isCellEmpty = (cell: any): boolean => {
    if (cell == null || cell === '' || cell === '-' || cell === '—' || cell === '–') return true;
    if (typeof cell === 'string') {
      const t = cell.trim().toLowerCase();
      if (t === '' || t === 'n/a' || t === 'na' || t === 'not available' || t === 'not specified') return true;
    }
    if (typeof cell === 'object' && 'value' in cell) return isCellEmpty((cell as TableCellValue).value);
    return false;
  };
  const visibleHeaders = useMemo(() => {
    const headers = data?.headers || [];
    const rows = data?.rows || [];
    if (rows.length === 0 || headers.length <= 1) return headers;
    return headers.filter(header => rows.some(row => !isCellEmpty(row[header])));
  }, [data]);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth();

    const resizeObserver = new ResizeObserver(updateContainerWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);


  const sortedData = useMemo(() => {
    if (!sortConfig || !sortable) {
      return data.rows;
    }

    return [...data.rows].sort((a, b) => {
      const aValue = getCellValue(a[sortConfig.key]);
      const bValue = getCellValue(b[sortConfig.key]);

      if (aValue === 'N/A' && bValue !== 'N/A') return 1;
      if (bValue === 'N/A' && aValue !== 'N/A') return -1;
      if (aValue === 'N/A' && bValue === 'N/A') return 0;

      if (aValue === '' && bValue !== '') return 1;
      if (bValue === '' && aValue !== '') return -1;
      if (aValue === '' && bValue === '') return 0;

      if (aValue === null && bValue !== null) return 1;
      if (bValue === null && aValue !== null) return -1;
      if (aValue === null && bValue === null) return 0;

      let aCompare: string | number = aValue as string | number;
      let bCompare: string | number = bValue as string | number;

      const aNum = parseFloat(String(aValue).replace(/[^\d.-]/g, ''));
      const bNum = parseFloat(String(bValue).replace(/[^\d.-]/g, ''));

      if (!isNaN(aNum) && !isNaN(bNum)) {
        aCompare = aNum;
        bCompare = bNum;
      } else {
        aCompare = String(aValue).toLowerCase();
        bCompare = String(bValue).toLowerCase();
      }

      if (aCompare < bCompare) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aCompare > bCompare) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [data.rows, sortConfig, sortable]);

  const handleSort = (key: string) => {
    if (!sortable) return;

    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (header: string) => {
    if (!sortable) return null;

    if (!sortConfig || sortConfig.key !== header) {
      return <span className="table-view__sort-icon table-view__sort-icon--neutral">⇅</span>;
    }

    return (
      <span className={`table-view__sort-icon table-view__sort-icon--${sortConfig.direction}`}>
        {sortConfig.direction === 'ascending' ? '↑' : '↓'}
      </span>
    );
  };

  const parseHtmlLinks = (html: string): React.ReactNode => {
    const linkRegex = /<a\s+([^>]*\s+)?href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(html)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const textBefore = html.slice(lastIndex, match.index);
        if (textBefore) {
          parts.push(textBefore);
        }
      }

      const fullMatch = match[0];
      const href = match[2];
      const linkText = match[3];

      let fullUrl = href;
      if (!href.startsWith('http://') && !href.startsWith('https://')) {
        fullUrl = `https://${href}`;
      }

      parts.push(
        <a
          key={`link-${match.index}`}
          href={fullUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="table-view__link"
          title={fullUrl}
        >
          {linkText}
        </a>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < html.length) {
      const textAfter = html.slice(lastIndex);
      if (textAfter) {
        parts.push(textAfter);
      }
    }

    return parts.length > 0 ? parts : html;
  };

  const formatCellValue = (cellData: string | number | null | TableCellValue) => {
    const value = getCellValue(cellData);
    const isCellObj = cellData && typeof cellData === 'object' && 'value' in cellData;
    const isFilled = isCellObj && (cellData as TableCellValue).filled;
    const isVerified = isCellObj ? (cellData as TableCellValue).verified : undefined;
    const sourceUrl = isCellObj ? (cellData as TableCellValue).sourceUrl : undefined;
    const calculationDetails = isCellObj ? (cellData as TableCellValue).calculationDetails : undefined;
    // Derived values (LLM-supplied formula) are treated visually like CAGR-projected values: blue * marker, suppress the orange unverified warning.
    const isDerived = !!calculationDetails;
    const showAsCalculated = isFilled || isDerived;
    const hasVerifiedSource = isVerified === true && typeof sourceUrl === 'string' && sourceUrl.trim().length > 0;

    // Handle null, N/A, or empty values
    if (value === null || value === 'N/A' || value === '') {
      return (
        <span className={`table-view__na-value ${isFilled ? 'table-view__cell--filled' : ''}`} />
      );
    }

    const stringValue = String(value);
    const isNumericLikeValue = isNumericLikePrimitive(value);

    // Hide unverified numeric-like cell — but keep derived values (calculationDetails present) visible with blue * marker.
    if (hideUnverified && isNumericLikeValue && !isFilled && isVerified !== true && !calculationDetails) {
      return (
        <span className="table-view__unverified-hidden" title="Unverified value" />
      );
    }

    // Check for HTML links first (before numeric processing)
    const htmlLinkRegex = /<a\s+([^>]*\s+)?href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/i;
    if (htmlLinkRegex.test(stringValue)) {
      return (
        <span className={`table-view__text-with-links ${isFilled ? 'table-view__cell--filled' : ''}`}>
          {parseHtmlLinks(stringValue)}
        </span>
      );
    }

    // Check for URLs (before numeric processing)
    const urlRegex = /(https?:\/\/(?:[-\w.])+(?:\:[0-9]+)?(?:\/(?:[\w\/_.])*)?(?:\?(?:[\w&%_.])*)?(?:\#(?:[\w.])*)?)/gi;
    if (urlRegex.test(stringValue)) {
      return (
        <span className={`table-view__text-with-links ${isFilled ? 'table-view__cell--filled' : ''}`}>
          {parseUrlsInText(stringValue)}
        </span>
      );
    }

    // Handle numeric values - be more defensive
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
      const inner = (
        <span
          className={`table-view__numeric-value ${showAsCalculated ? 'table-view__cell--filled' : ''} ${calculationDetails ? 'table-view__cell--calculated' : ''} ${isVerified === false && !isDerived ? 'table-view__cell--unverified' : ''}`}
          title={calculationDetails || (isVerified === false ? 'Unverified value' : undefined)}
        >
          {formatNumber(value)}
        </span>
      );
      if (hasVerifiedSource) {
        return (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="table-view__source-link">
            {inner}
          </a>
        );
      }
      return inner;
    }

    // Try to parse string as number - but be careful about strings with text context
    // Don't format if the string contains text like "by 2050", "estimated", "planned", etc.
    const containsTextContext = /\b(by|in|around|approximately|estimated|planned|expected|projected)\b/i.test(stringValue);

    if (!containsTextContext) {
      const numericValue = parseFloat(stringValue.replace(/[^\d.-]/g, ''));
      if (!isNaN(numericValue) && isFinite(numericValue)) {
        // Check if the original string looks like a pure number with optional currency/unit
        // This pattern is more strict: number followed by optional unit/percentage, but NOT text or year ranges
        const numericMatch = stringValue.match(/^[$€£¥]?\s*-?\d+([.,]\d+)?([KMBkmb]|%)?$/);
        if (numericMatch) {
          const inner = (
            <span
              className={`table-view__numeric-value ${showAsCalculated ? 'table-view__cell--filled' : ''} ${calculationDetails ? 'table-view__cell--calculated' : ''} ${isVerified === false && !isDerived ? 'table-view__cell--unverified' : ''}`}
              title={calculationDetails || (isVerified === false ? 'Unverified value' : undefined)}
            >
              {formatNumber(numericValue)}
            </span>
          );
          if (hasVerifiedSource) {
            return (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="table-view__source-link">
                {inner}
              </a>
            );
          }
          return inner;
        }
      }
    }

    const displayValue = stringValue;
    return showAsCalculated ? (
      <span className={`table-view__cell--filled ${calculationDetails ? 'table-view__cell--calculated' : ''} ${isVerified === false && !isDerived ? 'table-view__cell--unverified' : ''}`} title={calculationDetails || (isVerified === false ? 'Unverified value' : undefined)}>{displayValue}</span>
    ) : hasVerifiedSource ? (
      <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="table-view__source-link">
        {displayValue}
      </a>
    ) : (
      displayValue
    );
  };

  // Tab-separated rows including the header line. Used for plain-text clipboard copy
  // (so column titles are always present) and the screenshot fallback. Numbers stay
  // link-free so the user can paste number columns straight into a calculation.
  const buildPlainTable = () => [
    visibleHeaders.join('\t'),
    ...sortedData.map(row =>
      visibleHeaders.map(header => cleanValueForExport(row[header], COPY_EXPORT_OPTS)).join('\t')
    )
  ].join('\n');

  // Bare <table> fragment for the rich-clipboard copy, so pasting into Word/Excel keeps
  // the columns. Same calculation-friendly rendering as the plain copy.
  const buildHtmlTable = () => [
    '<table border="1">',
    '<thead><tr>',
    ...visibleHeaders.map(header => `<th>${header}</th>`),
    '</tr></thead>',
    '<tbody>',
    ...sortedData.map(row =>
      '<tr>' +
      visibleHeaders.map(header => `<td>${cleanValueForExport(row[header], COPY_EXPORT_OPTS)}</td>`).join('') +
      '</tr>'
    ),
    '</tbody></table>'
  ].join('');

  const exportToCSV = () => {
    const csvContent = [
      visibleHeaders.join(','),
      ...sortedData.map(row =>
        visibleHeaders.map(header => {
          const value = cleanValueForExport(row[header]);
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = () => {
    const jsonData = {
      headers: visibleHeaders,
      rows: sortedData.map(row => {
        const cleanRow: Record<string, string | number> = {};
        visibleHeaders.forEach(header => {
          cleanRow[header] = cleanValueForExport(row[header]);
        });
        return cleanRow;
      }),
      exportDate: new Date().toISOString(),
      totalRows: sortedData.length
    };

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = () => {
    const toSheet = (opts?: ExportValueOptions) => [
      visibleHeaders,
      ...sortedData.map(row => visibleHeaders.map(header => cleanValueForExport(row[header], opts)))
    ];
    downloadXlsx(filename, [
      // Sheet 1 — calculation-friendly: numbers bare, strings keep links.
      { name: 'Data', aoa: toSheet(COPY_EXPORT_OPTS) },
      // Sheet 2 — full: every cell keeps its source link, calculated numbers keep the formula.
      { name: 'Sources and Formulas', aoa: toSheet() }
    ]);
  };

  const handleExport = (format: string) => {
    if (onExport) {
      onExport(format, { headers: data.headers, rows: sortedData });
      return;
    }

    switch (format) {
      case 'csv':
        exportToCSV();
        break;
      case 'json':
        exportToJSON();
        break;
      case 'excel':
        exportToExcel();
        break;
      default:
        console.warn(`Export format "${format}" not supported`);
    }
  };

  // First-class "Copy table" action: copies headers + rows as both rich HTML (keeps
  // columns/links when pasted into Word/Excel) and tab-separated plain text.
  const copyTable = async () => {
    const ok = await new ClipboardService(document).copyRich(buildHtmlTable(), buildPlainTable());
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getExportButtonLabel = (format: string) => {
    switch (format) {
      case 'csv': return 'Export CSV';
      case 'json': return 'Export JSON';
      case 'excel': return 'Export Excel';
      default: return `Export ${format.toUpperCase()}`;
    }
  };

  const getExportButtonIcon = (format: string) => {
    switch (format) {
      case 'csv': return 'download';
      case 'json': return 'download';
      case 'excel': return 'download';
      default: return 'save';
    }
  };

  const takeScreenshot = async () => {
    try {
      const tableWrapper = tableWrapperRef.current;
      if (!tableWrapper) return;

      const tableElement = tableWrapper.querySelector('table') as HTMLElement;
      if (!tableElement) return;

      // Load html2canvas from CDN if not already available
      let html2canvas = (window as any).html2canvas;
      if (!html2canvas) {
        try {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('load failed'));
            document.head.appendChild(script);
          });
          html2canvas = (window as any).html2canvas;
        } catch {
          html2canvas = null;
        }
      }

      if (html2canvas) {
        // Capture the whole container (table + the "Missing data" note) so the
        // note travels with the screenshot. The export buttons are dropped from
        // the clone, and we subtract their footprint from the height so no blank
        // gap is left behind them.
        const container = containerRef.current;
        const fullWidth = tableElement.scrollWidth;

        const exportSection = container?.querySelector('.table-view__export-section') as HTMLElement | null;
        let removedHeight = 0;
        if (exportSection) {
          const cs = getComputedStyle(exportSection);
          removedHeight = exportSection.offsetHeight + parseFloat(cs.marginTop || '0') + parseFloat(cs.marginBottom || '0');
        }
        const fullHeight = (container?.scrollHeight ?? tableElement.scrollHeight) - removedHeight;

        const canvas = await html2canvas(container ?? tableElement, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false,
          width: fullWidth,
          height: fullHeight,
          scrollX: 0,
          scrollY: 0,
          onclone: (_clonedDoc: Document, clonedElement: HTMLElement) => {
            clonedElement.style.width = `${fullWidth}px`;
            clonedElement.style.overflow = 'visible';
            clonedElement.style.backgroundColor = '#ffffff';
            // Drop the export buttons — they shouldn't appear in the screenshot.
            clonedElement.querySelector('.table-view__export-section')?.remove();
            // Undo any wrapper clipping so the full table renders.
            const wrapper = clonedElement.querySelector('.table-view__wrapper') as HTMLElement | null;
            if (wrapper) {
              wrapper.style.maxHeight = 'none';
              wrapper.style.overflow = 'visible';
            }
            // Ensure no cell text gets cut off
            clonedElement.querySelectorAll<HTMLElement>('th, td').forEach(cell => {
              cell.style.whiteSpace = 'normal';
              cell.style.overflow = 'visible';
            });
          },
        });

        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}-${new Date().toISOString().split('T')[0]}.png`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, 'image/png');
      } else {
        const tableHtml = tableWrapper.outerHTML;
        const styledHtml = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Table Screenshot - ${filename}</title>
              <meta charset="utf-8">
              <style>
                body { 
                  margin: 20px; 
                  font-family: system-ui, -apple-system, sans-serif; 
                  background: #f5f5f5;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: calc(100vh - 40px);
                }
                .table-view__wrapper {
                  background: #ffffff;
                  padding: 16px;
                  border: 1px solid #e0e0e0;
                  border-radius: 8px;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                  max-height: none !important;
                  overflow: visible !important;
                }
                .table-view {
                  border-collapse: collapse;
                  width: 100%;
                  font-size: 14px;
                }
                .table-view th,
                .table-view td {
                  border: 1px solid #ddd;
                  padding: 8px;
                  text-align: left;
                }
                .table-view th {
                  background-color: #f5f5f5;
                  font-weight: bold;
                }
                .table-view--striped tbody tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
                .table-view__link {
                  color: #007bff;
                  text-decoration: none;
                }
                .table-view__link:hover {
                  text-decoration: underline;
                }
                .table-view__na-value {
                  color: #6c757d;
                  font-style: italic;
                }
                .table-view__numeric-value {
                  font-weight: 500;
                }
                .caption {
                  font-size: 18px;
                  font-weight: bold;
                  margin-bottom: 10px;
                  text-align: center;
                }
                .screenshot-info {
                  position: fixed;
                  top: 10px;
                  right: 10px;
                  background: #007bff;
                  color: white;
                  padding: 8px 12px;
                  border-radius: 4px;
                  font-size: 12px;
                  z-index: 1000;
                }
              </style>
            </head>
            <body>
              <div class="screenshot-info">
                Right-click → "Save as..." or use browser's screenshot tool
              </div>
              ${caption ? `<div class="caption">${caption}</div>` : ''}
              ${tableHtml}
            </body>
          </html>
        `;

        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(styledHtml);
          newWindow.document.close();

          newWindow.focus();

          setTimeout(() => {
            alert('Screenshot window opened! You can:\n1. Right-click and "Save as..." to save the HTML file\n2. Use your browser\'s screenshot feature\n3. Use Cmd+Shift+4 (Mac) or Print Screen (Windows) to capture the table');
          }, 500);
        }
      }
    } catch (error) {
      console.warn('Screenshot failed:', error);
      try {
        await navigator.clipboard.writeText(buildPlainTable());
        alert('Screenshot not available, but table data has been copied to clipboard as tab-separated values!');
      } catch (clipboardError) {
        alert('Screenshot feature not available. Please use the export buttons or manually capture the table.');
      }
    }
  };

  const tableClasses = [
    'table-view',
    striped && 'table-view--striped',
    bordered && 'table-view--bordered',
    hover && 'table-view--hover',
    sortable && 'table-view--sortable',
    className
  ].filter(Boolean).join(' ');

  const wrapperClasses = [
    'table-view__wrapper',
    responsive && 'table-view__wrapper--responsive'
  ].filter(Boolean).join(' ');

  // Calculate dynamic max-width for header cells.
  // When the table is wider than the container, let each column keep its natural
  // width so the wrapper's overflow-x:auto handles scrolling — never clamp columns
  // so tightly that the last column (e.g. SOURCE) gets hidden.
  const getHeaderCellMaxWidth = (header: string) => {
    if (!containerWidth) return 'auto';

    const columnCount = visibleHeaders.length;
    const padding = 32;
    const availableWidth = containerWidth - padding;
    const idealWidthPerColumn = availableWidth / columnCount;

    // Link-heavy columns (Source, URL, Link…) need more room
    const isLinkColumn = /source|url|link|ref/i.test(header);
    const minWidth = isLinkColumn ? 140 : 80;
    const maxWidth = isLinkColumn ? 300 : 280;

    // If the ideal width is already larger than our max, allow horizontal scroll
    // instead of crushing every column — return 'none' so wrapper can scroll.
    if (idealWidthPerColumn >= minWidth) {
      return `${Math.min(maxWidth, Math.round(idealWidthPerColumn))}px`;
    }

    // Table is too wide for the container → let it scroll naturally
    return 'none';
  };

  return (
    <div className="table-view__container" ref={containerRef}>
      <div ref={tableWrapperRef} className={wrapperClasses} style={{ maxHeight }}>
        <table className={tableClasses}>
          {caption && <caption className="table-view__caption">{caption}</caption>}
          <thead className="table-view__header">
            <tr>
              {visibleHeaders.map((header, index) => (
                <th
                  key={index}
                  className={`table-view__header-cell ${sortable ? 'table-view__header-cell--sortable' : ''}`}
                  onClick={() => handleSort(header)}
                  role={sortable ? 'button' : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  style={{ maxWidth: getHeaderCellMaxWidth(header) }}
                  onKeyDown={(e) => {
                    if (sortable && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleSort(header);
                    }
                  }}
                >
                  <span className="table-view__header-content">
                    <span title={header}>{header}</span>
                    {getSortIcon(header)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-view__body">
            {sortedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="table-view__row">
                {visibleHeaders.map((header, cellIndex) => (
                  <td key={cellIndex} className="table-view__cell">
                    {formatCellValue(row[header])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showExportButtons && (
        <div className="table-view__export-section">
          <div className="table-view__export-buttons">
            {exportFormats.map((format) => (
              <IconButton
                iconName={getExportButtonIcon(format)}
                text={getExportButtonLabel(format)}
                title={`Export table data as ${format.toUpperCase()}`}
                handleClick={() => handleExport(format)}
                color="green"
                key={format}
              />
            ))}
            <IconButton
              iconName={copied ? 'check' : 'content_copy'}
              text={copied ? 'Copied!' : 'Copy'}
              title="Copy the table (with column titles) to the clipboard"
              handleClick={copyTable}
              color="green"
            />
            <IconButton
              iconName="fullscreen"
              text="Screenshot"
              title="Take a screenshot of the table"
              handleClick={takeScreenshot}
              color="purple"
            />
          </div>
        </div>
      )}

      {(isFilled || (data?.rows || []).some(row => Object.values(row).some((c: any) => c && typeof c === 'object' && c.calculationDetails))) && <InfoMessage
        message={'Missing data has been automatically filled using calculation methods (e.g., CAGR projections, stoichiometric or conversion-ratio derivations). Calculated values are displayed in <span style="color: var(--secondary-color-500); font-weight: bold;">blue *</span> — hover any value to see the formula used.'}
        icon="info"
      />
      }
    </div>
  );
};

export default TableView;
